import { useState, useCallback } from 'react';
import { MessageSquare, RefreshCw, ChevronDown, ChevronRight } from 'lucide-react';
import { ApiService } from '../services/apiService';
import { useViewMode } from '../hooks/useViewMode';

export const SessionMemoryPanel: React.FC = () => {
  const { viewMode } = useViewMode();
  const [isOpen, setIsOpen] = useState(false);
  const [memory, setMemory] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const apiService = ApiService.getInstance();

  const fetchMemory = useCallback(async () => {
    const sessionId = sessionStorage.getItem('researchSessionId');
    if (!sessionId) {
      setError('No active session');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await apiService.getSessionMemory(sessionId);
      setMemory(result.exists ? result.memory : null);
    } catch {
      setError('Failed to load session memory');
    } finally {
      setLoading(false);
    }
  }, [apiService]);

  if (viewMode !== 'admin') return null;

  const handleToggle = () => {
    const opening = !isOpen;
    setIsOpen(opening);
    if (opening && !memory && !loading) {
      fetchMemory();
    }
  };

  return (
    <div data-component="SessionMemoryPanel" className="mb-4 border border-emerald-200 rounded-lg bg-emerald-50">
      <button
        onClick={handleToggle}
        className="w-full flex items-center gap-2 px-4 py-2 text-sm font-medium text-emerald-800 hover:bg-emerald-100 rounded-t-lg"
      >
        {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        <MessageSquare className="w-4 h-4" />
        Session Memory
        {memory?.turns?.length > 0 && (
          <span className="ml-1 px-1.5 py-0.5 text-xs bg-emerald-200 text-emerald-800 rounded">
            {memory.turns.length} turns
          </span>
        )}
      </button>

      {isOpen && (
        <div data-flow="open" className="px-4 pb-4 space-y-2">
          <div className="flex justify-end">
            <button
              onClick={fetchMemory}
              disabled={loading}
              className="flex items-center gap-1 px-2 py-1 text-xs text-emerald-700 hover:bg-emerald-100 rounded"
            >
              <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>

          {error && <div data-flow="error" className="text-sm text-red-600">{error}</div>}
          {loading && <div data-flow="loading" className="text-sm text-gray-500">Loading...</div>}

          {!loading && !memory && !error && (
            <div className="text-sm text-gray-500">No session data yet. Submit a query first.</div>
          )}

          {!loading && memory?.turns?.length > 0 && (
            <div className="space-y-2">
              {memory.turns.map((turn: any, i: number) => (
                <div key={i} className="p-2 bg-white border border-emerald-100 rounded text-xs">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-emerald-900">Turn {turn.turnIndex + 1}</span>
                    <span className={`px-1.5 py-0.5 rounded text-xs ${
                      turn.status === 'completed' ? 'bg-green-100 text-green-700' :
                      turn.status === 'blocked' ? 'bg-red-100 text-red-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {turn.status}
                    </span>
                  </div>
                  <div className="text-gray-700 mb-0.5">
                    <strong>Q:</strong> {turn.query?.length > 100 ? turn.query.slice(0, 100) + '...' : turn.query}
                  </div>
                  <div className="text-gray-600">
                    <strong>A:</strong> {turn.answerSummary?.length > 150 ? turn.answerSummary.slice(0, 150) + '...' : turn.answerSummary}
                  </div>
                  {turn.opinionPassages?.length > 0 && (
                    <div className="mt-1 text-gray-500">
                      Sources: {turn.opinionPassages.map((p: any) => p.opinionId).join(', ')}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
